import { Component, ViewChild, inject, OnInit, OnDestroy, effect, signal, computed } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

import { BreadcrumbModule } from 'primeng/breadcrumb';
import { Popover, PopoverModule } from 'primeng/popover';
import { Router, RouterLink } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { AgGridAngular } from 'ag-grid-angular';
import {
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IGetRowsParams,
  RowClickedEvent,
  ModuleRegistry,
  InfiniteRowModelModule,
  GridOptions,
  ColumnState,
} from 'ag-grid-community';
import { getDataSource } from '@shared/utils/aggrid-datasource';
import { ThemeService } from '@shared/services/theme.service';
import { getAgGridTheme } from '@shared/utils/aggrid-themes';
import { Pacientes } from '@pacientes/services/pacientes';
import { getColDefs } from './column-def';
import { GridLoadingOverlay } from '@shared/components/grid-overlays/grid-loading-overlay';
import { GridNoRowsOverlay } from '@shared/components/grid-overlays/grid-no-rows-overlay';
import { InteractiveNavigator } from '@shared/components/interactive-navigator/interactive-navigator';
import { NavigatorDefinition } from '@shared/models/navigator.t';
import { AppConfig } from '@shared/services/app-config.service';
import { PacientesViewService } from '@pacientes/services/pacientes-view.service';
import { PacienteRowData } from '@pacientes/models/pacientes-view.models';
import { GridStateService } from '@shared/services/grid-state.service';
import { StatusBar } from '@shared/components/status-bar/status-bar';
import { PermissionService, RECURSOS } from '@auth/services/permission.service';
import { ActionOverlay } from '../action-overlay/action-overlay';
import { FormsModule } from '@angular/forms';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DividerModule } from 'primeng/divider';
import { PacientesToggleState, mergeToggleFilters } from './pacientes-filters';

ModuleRegistry.registerModules([InfiniteRowModelModule]);
type CrearPacienteEvent = Record<string, unknown>;

interface FilterChip {
  id: string;
  type: 'filter' | 'sort';
  colId: string;
  label: string;
}

const COLUMN_LABELS: Record<string, string> = {
  numero_documento: 'Documento',
  nombre_completo: 'Paciente',
  tratamiento: 'Tratamiento',
  departamento_tratamiento: 'Dpto. Tratamiento',
  municipio_tratamiento: 'Ciudad Tratamiento',
  fecha_inicio_tratamiento: 'Inicio TTO',
  fecha_egreso: 'Egreso',
  condicion_egreso: 'Condición Egreso',
  georreferenciacion: 'Georreferenciación',
  fecha_creacion: 'Creación',
};

const TRATAMIENTO_ABBR: Record<string, string> = {
  'TUBERCULOSIS SENSIBLE': 'TBS',
  'TUBERCULOSIS FARMACORRESISTENTE': 'TBF',
  'QUIMIOPROFILAXIS': 'QMO',
  'LEPRA': 'LPA',
};

@Component({
  selector: 'app-pacientes-list',
  standalone: true,
  imports: [
    ButtonModule,
    TooltipModule,
    TagModule,
    BreadcrumbModule,
    PopoverModule,
    RouterLink,
    AgGridAngular,
    InteractiveNavigator,
    StatusBar,
    ActionOverlay,
    ConfirmDialogModule,
    ChipModule,
    ToggleButtonModule,
    DividerModule,
    FormsModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './pacientes-list.html',
  styleUrl: './pacientes-list.css',
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
  },
})
export class PacientesList implements OnInit, OnDestroy {
  @ViewChild('op') op!: Popover;
  appConfigService = inject(AppConfig);
  router = inject(Router);
  protected perm = inject(PermissionService);

  title = 'Pacientes';
  componentLocation = 'crear_paciente_TBS';
  items: MenuItem[] | undefined;
  pacientesService = inject(Pacientes);
  private themeService = inject(ThemeService);
  pacientesViewService = inject(PacientesViewService);
  private gridStateService = inject(GridStateService);
  private confirmationService = inject(ConfirmationService);

  public gridApi!: GridApi;
  private readonly GRID_NAME = 'pacientes';
  public gridOptions = signal<GridOptions>({
    rowModelType: 'infinite',
    cacheBlockSize: 100,
    cacheOverflowSize: 2,
    maxBlocksInCache: 10,
    maxConcurrentDatasourceRequests: 1,
    loadingOverlayComponent: GridLoadingOverlay,
    noRowsOverlayComponent: GridNoRowsOverlay,
  });
  public isDarkMode = this.themeService.getDarkMode();
  public activeChips = signal<FilterChip[]>([]);
  public hasActiveFilters = computed(() => this.activeChips().length > 0);

  // Vigilancia filter. NOT a grid column — its state is injected into the
  // server-side filterModel (see ./pacientes-filters). The
  // "bacteriológicamente confirmado" filter stays wired in the backend/helper
  // but is hidden from the UI for now (algoritmo pendiente de definición).
  public investigacionEpidemiologica = signal(false);
  public georreferenciacionFilter = signal(false);
  public pacientesEgresadosFilter = signal(false);
  public anyToggleActive = computed(
    () =>
      this.investigacionEpidemiologica() ||
      this.georreferenciacionFilter() ||
      this.pacientesEgresadosFilter(),
  );
  private toggleState = computed<PacientesToggleState>(() => ({
    investigacionEpidemiologica: this.investigacionEpidemiologica(),
    bacteriologicamenteConfirmado: false,
    georreferenciacion: this.georreferenciacionFilter(),
    pacientesEgresados: this.pacientesEgresadosFilter(),
  }));

  bcItems: MenuItem[] = [
    { label: 'Pacientes' },
    { label: 'Form' },
    { label: 'InputText', routerLink: '/inputtext' },
  ];

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  navigatorOpened = signal(false);
  navigatorItems: NavigatorDefinition = {
    name: 'Crear Paciente',
    items: [
      {
        name: 'Tuberculosis Sensible',
        route: '/pacientes/crear_paciente_TBS',
        imageSrc: 'assets/icons/create-patient/tuberculosis_sensible.png',
        recurso: RECURSOS.CREAR_PACIENTE_TBS,
      },
      {
        name: 'Tuberculosis Farmacorresistente',
        route: '/pacientes/crear_paciente_TBF',
        imageSrc: 'assets/icons/create-patient/tuberculosis_farmacorresistente.png',
        recurso: RECURSOS.CREAR_PACIENTE_TBF,
      },
      {
        name: 'Quimioprofilaxis',
        route: '/pacientes/crear_paciente_QMO',
        imageSrc: 'assets/icons/create-patient/quimioprofilaxis.png',
        recurso: RECURSOS.CREAR_PACIENTE_QMO,
      },
      {
        name: 'Lepra',
        route: '/pacientes/crear_paciente_LPA',
        imageSrc: 'assets/icons/create-patient/lepra.png',
        recurso: RECURSOS.CREAR_PACIENTE_LPA,
      },
    ],
  };

  constructor() {
    effect(() => {
      const isDark = this.isDarkMode();
      if (this.gridApi) {
        const theme = getAgGridTheme(isDark);
        this.gridApi.setGridOption('theme', theme);
      }
    });
  }

  ngOnInit() {
    this.items = [
      {
        items: [
          {
            label: 'Tuberculosis Sensible',
            icon: 'pi pi-angle-right',
          },
          {
            label: 'Tuberculosis Farmacorresistente',
            icon: 'pi pi-angle-right',
          },
          {
            label: 'Quimioprofilaxis',
            icon: 'pi pi-angle-right',
          },
          {
            label: 'Lepra',
            icon: 'pi pi-angle-right',
          },
        ],
      },
    ];
    this.loadData(this.componentLocation);
  }

  public onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
    this.gridApi.setGridOption('rowSelection', {
      mode: 'singleRow',
      checkboxes: false,
      enableClickSelection: true,
    });

    // Set column definitions
    const columnDefs = getColDefs();
    this.gridApi.setGridOption('columnDefs', columnDefs);

    // Set default column definition
    this.gridApi.setGridOption('defaultColDef', {
      suppressMovable: true,
      sortable: true,
      filter: true,
      resizable: true,
    });

    // Set theme
    const initialTheme = getAgGridTheme(this.isDarkMode());
    this.gridApi.setGridOption('theme', initialTheme);

    // Restore saved filter and sort state before setting datasource
    this.gridStateService.restoreState(this.GRID_NAME, this.gridApi);

    // Restore the persisted vigilancia toggle filters before the datasource
    // runs, so its first fetch already reflects the saved toggle state.
    this.restoreToggleFilters();

    // Inject the active toggle filters into the grid's own filterModel before
    // each request; toggleState() is read at call time, so purging the cache
    // after a toggle change re-fetches with the new conditions.
    const dataSource = getDataSource(
      (params: IGetRowsParams) =>
        this.pacientesService.obtenerPacientesView({
          ...params,
          filterModel: mergeToggleFilters(
            params.filterModel as Record<string, unknown>,
            this.toggleState(),
          ),
        }),
      undefined,
      this.gridApi,
    );
    this.gridApi.setGridOption('datasource', dataSource);

    this.pacientesViewService.registerOnDeleteSuccess(() => this.gridApi?.purgeInfiniteCache());

    this.buildChips();
  }

  public onFilterChanged(): void {
    if (this.gridApi) {
      this.gridStateService.saveState(this.GRID_NAME, this.gridApi);
      this.buildChips();
    }
  }

  public onSortChanged(): void {
    if (this.gridApi) {
      this.gridStateService.saveState(this.GRID_NAME, this.gridApi);
      this.buildChips();
    }
  }

  public onInvestigacionToggle(checked: boolean): void {
    this.investigacionEpidemiologica.set(checked);
    this.persistToggleFilters();
    this.refreshGridData();
  }

  public onGeorreferenciacionToggle(checked: boolean): void {
    this.georreferenciacionFilter.set(checked);
    this.persistToggleFilters();
    this.refreshGridData();
  }

  public onPacientesEgresadosToggle(checked: boolean): void {
    this.pacientesEgresadosFilter.set(checked);
    this.persistToggleFilters();
    this.refreshGridData();
  }

  private refreshGridData(): void {
    // Infinite row model: drop cached blocks so the datasource re-runs with the
    // current toggle state and the total row count is recomputed.
    this.gridApi?.purgeInfiniteCache();
  }

  private persistToggleFilters(): void {
    this.gridStateService.saveToggleState(this.GRID_NAME, {
      investigacionEpidemiologica: this.investigacionEpidemiologica(),
      georreferenciacion: this.georreferenciacionFilter(),
      pacientesEgresados: this.pacientesEgresadosFilter(),
    });
  }

  private restoreToggleFilters(): void {
    const saved = this.gridStateService.restoreToggleState(this.GRID_NAME);
    if (!saved) return;
    this.investigacionEpidemiologica.set(saved['investigacionEpidemiologica'] ?? false);
    this.georreferenciacionFilter.set(saved['georreferenciacion'] ?? false);
    this.pacientesEgresadosFilter.set(saved['pacientesEgresados'] ?? false);
  }

  private buildChips(): void {
    if (!this.gridApi) return;

    const filterChips: FilterChip[] = [];
    const filterModel = this.gridApi.getFilterModel();

    for (const [colId, model] of Object.entries(filterModel)) {
      filterChips.push({
        id: `filter:${colId}`,
        type: 'filter',
        colId,
        label: this.formatFilterLabel(colId, model as Record<string, unknown>),
      });
    }

    const sortChips: FilterChip[] = [];
    const columnState = this.gridApi.getColumnState();
    const sortedCols = columnState
      .filter((col: ColumnState) => col.sort != null)
      .sort((a: ColumnState, b: ColumnState) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

    for (const col of sortedCols) {
      if (col.colId === 'fecha_creacion' && col.sort === 'desc' && sortedCols.length === 1 && filterChips.length === 0) {
        continue;
      }
      const colLabel = COLUMN_LABELS[col.colId] ?? col.colId;
      const arrow = col.sort === 'asc' ? '↑' : '↓';
      sortChips.push({
        id: `sort:${col.colId}`,
        type: 'sort',
        colId: col.colId,
        label: `${colLabel} ${arrow}`,
      });
    }

    this.activeChips.set([...filterChips, ...sortChips]);
  }

  private formatFilterLabel(colId: string, model: Record<string, unknown>): string {
    const colLabel = COLUMN_LABELS[colId] ?? colId;
    const filterType = model['filterType'] as string | undefined;

    if (filterType === 'text') {
      const type = model['type'] as string;
      const value = model['filter'] as string;
      if (type === 'equals') {
        return `${colLabel} es '${value}'`;
      }
      return `${colLabel} contiene '${value}'`;
    }

    if (filterType === 'dateRange') {
      const dateFrom = model['dateFrom'] as string | null;
      const dateTo = model['dateTo'] as string | null;
      const fromStr = dateFrom
        ? new Date(`${dateFrom}T00:00:00`).toLocaleDateString('es-CO')
        : '...';
      const toStr = dateTo
        ? new Date(`${dateTo}T00:00:00`).toLocaleDateString('es-CO')
        : '...';
      return `${colLabel}: ${fromStr} — ${toStr}`;
    }

    if (filterType === 'set') {
      const values = model['values'] as string[];
      const abbreviated = values.map(v => TRATAMIENTO_ABBR[v] ?? v);
      return `${colLabel}: ${abbreviated.join(', ')}`;
    }

    return colLabel;
  }

  public removeChip(chip: FilterChip): void {
    if (!this.gridApi) return;

    if (chip.type === 'filter') {
      const model = this.gridApi.getFilterModel();
      delete model[chip.colId];
      this.gridApi.setFilterModel(model);
    } else {
      this.gridApi.applyColumnState({
        state: [{ colId: chip.colId, sort: null }],
      });
    }
  }

  public clearAllFilters(): void {
    if (!this.gridApi) return;
    this.investigacionEpidemiologica.set(false);
    this.georreferenciacionFilter.set(false);
    this.pacientesEgresadosFilter.set(false);
    this.gridApi.setFilterModel({});
    this.gridApi.applyColumnState({ defaultState: { sort: null } });
    this.gridStateService.clearState(this.GRID_NAME);
    this.refreshGridData();
  }

  public onRowClicked(event: RowClickedEvent) {
    const rowData = event.data as PacienteRowData;
    this.pacientesViewService.setSelectedRow(rowData);
  }

  getTratamientoRenderer() {
    return (params: ICellRendererParams) => {
      const value = params.value;
      const severity = this.getSeverity(value);
      return `<p-tag value="${value}" severity="${severity}" style="font-size: 0.7rem"></p-tag>`;
    };
  }

  getSeverity(tratamiento: string): string {
    switch (tratamiento) {
      case 'TBS':
        return 'success';
      case 'TBF':
        return 'danger';
      case 'QMO':
        return 'warning';
      case 'LPA':
        return 'info';
      default:
        return 'secondary';
    }
  }

  crearPaciente(event: CrearPacienteEvent) {
    console.log('Crear paciente:', event);
  }

  toggle(event: MouseEvent) {
    this.op.toggle(event);
  }

  onEscapeKey() {
    if (this.navigatorOpened()) {
      this.navigatorOpened.set(false);
    }
  }

  clearGridSelection() {
    this.pacientesViewService.clearSelection();
    this.gridApi?.deselectAll();
  }

  loadData(componentLocation: string) {
    this.appConfigService.getFormListConfig(componentLocation).subscribe(data => {
      this.items = data.map(form => ({
        label: form.name,
      }));
    });
  }

  // Expose currentButtons from service
  get currentButtons() {
    return this.pacientesViewService.currentButtons;
  }

  // Expose isRowSelected from service
  get isRowSelected() {
    return this.pacientesViewService.isRowSelected;
  }

  // Get selected row from service
  get selectedRow() {
    return this.pacientesViewService.selectedRow;
  }

  // Handle button click - navigate or execute command
  handleButtonClick(button: { label?: string; route?: string; command?: () => void }) {
    if (button.label === 'Eliminar' && button.command) {
      const row = this.selectedRow();
      const name = row?.nombre_completo ?? 'este caso';
      this.confirmationService.confirm({
        message: `¿Está seguro de eliminar a <strong>${name}</strong>?`,
        header: 'Confirmar Eliminación',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Eliminar',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => button.command!(),
      });
      return;
    }
    if (button.command) {
      button.command();
    } else if (button.route) {
      const row = this.selectedRow();
      if (row) {
        const resolvedRoute = this.pacientesViewService.resolveRoute(button.route, row);
        const routeSegments = resolvedRoute.split('/').filter(segment => segment.length > 0);
        this.router.navigate(['/', ...routeSegments]);
      }
    }
  }

  // Get button class based on theme
  getButtonClass(lightClass: string, darkClass: string): string {
    return this.isDarkMode() ? darkClass : lightClass;
  }

  ngOnDestroy() {
    // Clean up the selected row state when component is destroyed
    this.pacientesViewService.clearSelection();
  }
}
